import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge as BadgeUI } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Trophy, Star, Flame, Target, Calendar, TrendingUp, 
  Lock, CheckCircle, Award, Zap, Clock, Medal
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Badges = () => {
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchBadges();
    fetchStats();
  }, []);

  const fetchBadges = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/badges`, { headers });
      setBadges(response.data);
    } catch (error) {
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/badges/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze': return 'bg-orange-900/30 border-orange-500/50 text-orange-400';
      case 'silver': return 'bg-gray-500/20 border-gray-400/50 text-gray-300';
      case 'gold': return 'bg-yellow-900/30 border-yellow-500/50 text-yellow-400';
      case 'platinum': return 'bg-cyan-900/30 border-cyan-400/50 text-cyan-300';
      case 'diamond': return 'bg-blue-900/30 border-blue-400/50 text-blue-300';
      case 'legendary': return 'bg-purple-900/30 border-purple-400/50 text-purple-300';
      default: return 'bg-card border-border text-muted-foreground';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'bronze': return <Medal className="w-4 h-4" />;
      case 'silver': return <Medal className="w-4 h-4" />;
      case 'gold': return <Trophy className="w-4 h-4" />;
      case 'platinum': return <Star className="w-4 h-4" />;
      case 'diamond': return <Zap className="w-4 h-4" />;
      case 'legendary': return <Award className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'weekly': return <Calendar className="w-5 h-5" />;
      case 'monthly': return <TrendingUp className="w-5 h-5" />;
      case 'streak': return <Flame className="w-5 h-5" />;
      case 'milestone': return <Trophy className="w-5 h-5" />;
      case 'session': return <Target className="w-5 h-5" />;
      default: return <Star className="w-5 h-5" />;
    }
  };

  const categories = [
    { id: 'all', name: 'All', icon: Star },
    { id: 'weekly', name: 'Weekly', icon: Calendar },
    { id: 'monthly', name: 'Monthly', icon: TrendingUp },
    { id: 'streak', name: 'Streaks', icon: Flame },
    { id: 'milestone', name: 'Milestones', icon: Trophy },
    { id: 'session', name: 'Sessions', icon: Target },
  ];

  const filteredBadges = selectedCategory === 'all' 
    ? badges 
    : badges.filter(b => b.category === selectedCategory);

  const earnedBadges = filteredBadges.filter(b => b.earned);
  const lockedBadges = filteredBadges.filter(b => !b.earned);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Loading badges...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-white tracking-tighter uppercase">
            Skill Badges
          </h1>
          <p className="text-muted-foreground mt-1">
            {stats?.earned_badges || 0} of {stats?.total_badges || 0} badges earned
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="badge-stats">
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Trophy className="w-6 h-6 mx-auto text-yellow-500 mb-2" />
                <p className="text-2xl font-bold text-white">{stats.earned_badges}</p>
                <p className="text-xs text-muted-foreground">Badges Earned</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Star className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className="text-2xl font-bold text-white">{stats.total_points.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Points</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Target className="w-6 h-6 mx-auto text-accent mb-2" />
                <p className="text-2xl font-bold text-white">{stats.completion_percentage}%</p>
                <p className="text-xs text-muted-foreground">Complete</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-2xl font-bold text-white">{stats.total_badges - stats.earned_badges}</p>
                <p className="text-xs text-muted-foreground">Remaining</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Category Progress */}
        {stats?.categories && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading text-white">Progress by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(stats.categories).map(([cat, data]) => (
                  <div key={cat} className="text-center">
                    <div className="flex justify-center mb-2">
                      {getCategoryIcon(cat)}
                    </div>
                    <p className="text-xs text-muted-foreground capitalize mb-1">{cat}</p>
                    <p className="text-sm font-bold text-white">
                      {data.earned}/{data.total}
                    </p>
                    <div className="w-full bg-background rounded-full h-1.5 mt-1">
                      <div 
                        className="bg-primary h-1.5 rounded-full"
                        style={{ width: `${(data.earned / data.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
              className={selectedCategory === cat.id ? "bg-primary" : ""}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className="w-4 h-4 mr-1" />
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Earned Badges */}
        {earnedBadges.length > 0 && (
          <div>
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Earned ({earnedBadges.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {earnedBadges.map((badge) => (
                <Card 
                  key={badge.id} 
                  className={`${getTierColor(badge.tier)} border`}
                  data-testid={`badge-${badge.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTierColor(badge.tier)}`}>
                        {getTierIcon(badge.tier)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-white text-sm truncate">{badge.name}</h3>
                          <BadgeUI variant="outline" className="text-xs capitalize">
                            {badge.tier}
                          </BadgeUI>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{badge.description}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-primary font-bold">+{badge.points} pts</span>
                          {badge.earned_at && (
                            <span className="text-xs text-muted-foreground">
                              {new Date(badge.earned_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Locked Badges */}
        {lockedBadges.length > 0 && (
          <div>
            <h2 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-muted-foreground" />
              Locked ({lockedBadges.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {lockedBadges.slice(0, 30).map((badge) => (
                <Card 
                  key={badge.id} 
                  className="bg-card/50 border-border/50 opacity-60"
                  data-testid={`badge-locked-${badge.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-background">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-muted-foreground text-sm truncate">{badge.name}</h3>
                          <BadgeUI variant="outline" className="text-xs capitalize opacity-50">
                            {badge.tier}
                          </BadgeUI>
                        </div>
                        <p className="text-xs text-muted-foreground/70 line-clamp-2">{badge.description}</p>
                        <span className="text-xs text-primary/50 font-bold mt-2 inline-block">+{badge.points} pts</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {lockedBadges.length > 30 && (
              <p className="text-center text-muted-foreground text-sm mt-4">
                + {lockedBadges.length - 30} more badges to unlock
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Badges;
